// src/components/admin/mail/mail.styles.ts
import styled, { keyframes } from 'styled-components';
import { motion } from 'framer-motion';

/**
 * Shared styling for the mail section only.
 *
 * The rest of this codebase duplicates its design tokens per file and has no ThemeProvider.
 * That convention is kept — introducing global tokens here would be a codebase-wide refactor
 * smuggled in with a feature. But the seven mail components must render as one contiguous
 * surface, which is a different problem from four unrelated sections each having their own
 * Button, so they share this one module. Same hardcoded hexes as everywhere else.
 *
 * NOTE: this project has no global `box-sizing: border-box`, so anything with padding and
 * width:100% declares it explicitly below.
 */

export const BRAND = '#990147';
export const BORDER = '#e2e8f0';
export const SURFACE = '#f8fafc';
export const MUTED = '#64748b';
export const TEXT = '#334155';

export const AccentBar = styled.div<{ $gradient: string }>`
    height: 4px;
    background: ${(p) => p.$gradient};
    border-radius: 16px 16px 0 0;
`;

/**
 * The admin shell is not a fixed-height app: MainContent carries a hardcoded 100px top
 * margin to clear the floating site header, and a real footer sits below. So height is
 * clamped rather than 100vh, which would hide the top under the header and produce a
 * second scrollbar.
 */
export const MailWorkspace = styled.div`
    display: grid;
    grid-template-columns: 210px minmax(300px, 360px) minmax(0, 1fr);
    height: clamp(560px, calc(100vh - 320px), 860px);
    background: #ffffff;
    border: 1px solid ${BORDER};
    border-top: none;
    border-radius: 0 0 16px 16px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    overflow: hidden;
    box-sizing: border-box;

    @media (max-width: 1399px) {
        grid-template-columns: 180px minmax(280px, 330px) minmax(0, 1fr);
    }

    /* Breaks at 1100, not the usual 1024: the shell sidebar is 280px and does not shrink
       until 768px, so at 1024 the real content width is only ~648px — not enough for three
       panes. Below this the folder rail becomes a horizontal strip and list/detail become
       push navigation. */
    @media (max-width: 1099px) {
        grid-template-columns: minmax(0, 1fr);
        grid-template-rows: auto minmax(0, 1fr);
        height: auto;
        min-height: 60vh;
    }
`;

/** min-width/min-height:0 are load-bearing — without them overflow-y never engages. */
export const Pane = styled.div`
    min-width: 0;
    min-height: 0;
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
`;

export const ListPane = styled(Pane)`
    border-right: 1px solid ${BORDER};

    @media (max-width: 1099px) {
        border-right: none;
    }
`;

export const DetailPane = styled(Pane)`
    background: #ffffff;
`;

export const ScrollArea = styled.div`
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
`;

export const PaneHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 1rem 1.25rem;
    border-bottom: 1px solid ${BORDER};
    background: ${SURFACE};
    flex-wrap: wrap;
    box-sizing: border-box;
`;

export const PaneTitle = styled.h3`
    margin: 0;
    font-size: 1rem;
    font-weight: 700;
    color: #1e293b;
`;

export const SubtleText = styled.span`
    font-size: 0.78rem;
    color: #94a3b8;
`;

export const Button = styled(motion.button)<{ $variant?: 'primary' | 'secondary' | 'danger' }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    padding: 0.7rem 1.2rem;
    border-radius: 8px;
    font-size: 0.88rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    box-sizing: border-box;
    max-width: 100%;

    ${(p) =>
        p.$variant === 'danger'
            ? `border: 1.5px solid #fecaca; background: #fef2f2; color: #dc2626;`
            : p.$variant === 'secondary'
            ? `border: 1.5px solid ${BORDER}; background: #ffffff; color: ${MUTED};`
            : `border: 1.5px solid transparent; background: linear-gradient(135deg, #990147 0%, #cc3366 100%); color: #ffffff;`}

    &:hover:not(:disabled) {
        transform: translateY(-1px);
        box-shadow: 0 4px 14px rgba(0, 0, 0, 0.12);
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
        transform: none;
    }

    &:focus-visible {
        outline: 3px solid rgba(153, 1, 71, 0.35);
        outline-offset: 2px;
    }
`;

export const IconButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border: 1.5px solid ${BORDER};
    background: #ffffff;
    color: ${MUTED};
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s ease;
    box-sizing: border-box;

    &:hover:not(:disabled) {
        background: ${SURFACE};
        color: ${BRAND};
        border-color: ${BRAND};
    }

    &:disabled {
        opacity: 0.4;
        cursor: not-allowed;
    }
`;

export const Input = styled.input`
    width: 100%;
    padding: 0.7rem 0.85rem;
    border: 1.5px solid ${BORDER};
    border-radius: 8px;
    font-size: 0.88rem;
    background: white;
    transition: all 0.3s ease;
    box-sizing: border-box;
    font-family: inherit;

    &:focus {
        outline: none;
        border-color: ${BRAND};
        box-shadow: 0 0 0 3px rgba(153, 1, 71, 0.1);
    }

    &::placeholder {
        color: #94a3b8;
    }
`;

export const SearchWrapper = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    width: 100%;
    box-sizing: border-box;

    svg.search-icon {
        position: absolute;
        left: 0.7rem;
        color: #94a3b8;
        pointer-events: none;
    }

    input {
        padding-left: 2.2rem;
        padding-right: 2.2rem;
    }

    button.clear {
        position: absolute;
        right: 0.5rem;
        border: none;
        background: none;
        color: #94a3b8;
        cursor: pointer;
        display: inline-flex;
        padding: 0.2rem;
        border-radius: 4px;

        &:hover {
            color: ${BRAND};
        }
    }
`;

export const FieldLabel = styled.label`
    display: block;
    font-size: 0.78rem;
    font-weight: 600;
    color: #64748b;
    margin-bottom: 0.3rem;
`;

export const Textarea = styled.textarea`
    width: 100%;
    padding: 0.9rem;
    border: 1.5px solid ${BORDER};
    border-radius: 8px;
    font-size: 0.9rem;
    min-height: 220px;
    resize: vertical;
    background: white;
    transition: all 0.3s ease;
    box-sizing: border-box;
    font-family: inherit;
    line-height: 1.6;

    &:focus {
        outline: none;
        border-color: ${BRAND};
        box-shadow: 0 0 0 3px rgba(153, 1, 71, 0.1);
    }
`;

export const ErrorText = styled.p`
    color: #dc2626;
    font-size: 0.8rem;
    margin: 0.3rem 0 0 0;
    font-weight: 500;
`;

/* ── modal ─────────────────────────────────────────────────────────────────────
   z-index 1100, not the 1000 used by AdminClients: the site header is also fixed at
   z-index 1000 and floats over the admin panel, winning only by DOM order.            */

export const ModalOverlay = styled(motion.div)`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.7);
    backdrop-filter: blur(8px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1100;
    padding: 1rem;
    overflow-y: auto;
`;

export const Modal = styled(motion.div)<{ $accent?: string }>`
    background: white;
    border-radius: 16px;
    width: 100%;
    max-width: 680px;
    max-height: 88vh;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
    border-left: ${(p) => (p.$accent ? `4px solid ${p.$accent}` : 'none')};
    box-sizing: border-box;

    @media (max-width: 768px) {
        margin: 0.5rem;
        max-height: 92vh;
    }
`;

export const ModalBody = styled.div`
    padding: 1.5rem 2rem;
    overflow-y: auto;
    box-sizing: border-box;

    @media (max-width: 768px) {
        padding: 1.2rem;
    }
`;

export const ModalFooter = styled.div`
    display: flex;
    gap: 0.75rem;
    justify-content: flex-end;
    padding: 1rem 2rem 1.5rem;
    flex-wrap: wrap;
    box-sizing: border-box;

    @media (max-width: 768px) {
        padding: 1rem 1.2rem 1.2rem;
    }
`;

export const ModalTitle = styled.h2`
    font-size: 1.5rem;
    font-weight: 700;
    background: linear-gradient(135deg, #333, #990147);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    margin: 0 0 1rem 0;
    line-height: 1.3;
`;

/* ── pagination ───────────────────────────────────────────────────────────────── */

export const Pagination = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 0.85rem 1.25rem;
    border-top: 1px solid ${BORDER};
    background: ${SURFACE};
    flex-wrap: wrap;
    box-sizing: border-box;
`;

export const PageButton = styled.button<{ $active?: boolean }>`
    padding: 0.5rem 0.75rem;
    border: 1.5px solid ${(p) => (p.$active ? BRAND : BORDER)};
    background: ${(p) => (p.$active ? BRAND : 'white')};
    color: ${(p) => (p.$active ? 'white' : MUTED)};
    border-radius: 8px;
    font-weight: 600;
    min-width: 38px;
    font-size: 0.82rem;
    cursor: pointer;
    transition: all 0.2s ease;
    box-sizing: border-box;

    &:hover:not(:disabled) {
        background: ${(p) => (p.$active ? BRAND : SURFACE)};
        transform: translateY(-1px);
    }

    &:disabled {
        opacity: 0.4;
        cursor: not-allowed;
        transform: none;
    }
`;

/* ── skeleton ─────────────────────────────────────────────────────────────────── */

const shimmer = keyframes`
    0%   { opacity: 0.55; }
    50%  { opacity: 1; }
    100% { opacity: 0.55; }
`;

export const SkeletonBar = styled.div<{ $width?: string; $height?: string }>`
    width: ${(p) => p.$width || '100%'};
    height: ${(p) => p.$height || '12px'};
    background: #f1f5f9;
    border-radius: 6px;
    animation: ${shimmer} 1.4s ease-in-out infinite;
`;

export const SkeletonRow = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
    padding: 1rem 1.25rem;
    border-bottom: 1px solid #f1f5f9;
    box-sizing: border-box;
`;
